import { createClient } from "@supabase/supabase-js";
import type { Database } from './database.types';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseKey) {
  console.warn('Missing Supabase environment variables. Running in offline mode.');
}

// Validate Supabase URL format
const validateSupabaseUrl = (url: string): boolean => {
  try {
    const urlObj = new URL(url);
    return urlObj.protocol === 'https:' && urlObj.hostname.includes('supabase.co');
  } catch {
    return false;
  }
};

// Create a mock client for development environments
const createMockClient = () => ({
  auth: {
    getSession: () => Promise.resolve({ data: { session: null }, error: null }),
    onAuthStateChange: () => ({ data: { subscription: { unsubscribe: () => {} } } }),
    signInWithPassword: () => Promise.resolve({ data: { user: null }, error: { message: 'Offline mode' } }),
    signUp: () => Promise.resolve({ error: { message: 'Offline mode' } }),
    signOut: () => Promise.resolve({ error: null }),
    resetPasswordForEmail: () => Promise.resolve({ error: { message: 'Offline mode' } }),
    resend: () => Promise.resolve({ error: { message: 'Offline mode' } }),
  },
  from: () => ({
    select: () => ({ data: [], error: null }),
    insert: () => ({ error: { message: 'Offline mode' } }),
    update: () => ({ error: { message: 'Offline mode' } }),
    delete: () => ({ error: { message: 'Offline mode' } }),
    order: () => ({ data: [], error: null }),
    limit: () => ({ data: [], error: null }),
  }),
  storage: {
    from: () => ({
      list: () => Promise.resolve({ data: [], error: null }),
      upload: () => Promise.resolve({ error: { message: 'Offline mode' } }),
      remove: () => Promise.resolve({ error: { message: 'Offline mode' } }),
      getPublicUrl: () => ({ data: { publicUrl: '' } }),
    }),
  },
});

export const supabase = (supabaseUrl && supabaseKey && validateSupabaseUrl(supabaseUrl)) 
  ? createClient<Database>(supabaseUrl, supabaseKey, {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: true,
        flowType: 'pkce',
        storage: window.localStorage,
        storageKey: 'supabase.auth.token',
      },
      global: {
        headers: { 
          'x-application-name': 'profe-jhony',
          'x-client-info': 'supabase-js/2.39.3'
        }
      },
      db: {
        schema: 'public'
      },
      realtime: {
        params: {
          eventsPerSecond: 10
        }
      }
    })
  : createMockClient() as any;

// Enhanced connection health check with webcontainer-specific handling
export const checkSupabaseConnection = async (): Promise<{
  isConnected: boolean;
  error?: string;
  details?: any;
}> => {
  // Skip connection check if no valid Supabase config
  if (!supabaseUrl || !supabaseKey || !validateSupabaseUrl(supabaseUrl)) {
    return {
      isConnected: false,
      error: 'Configuración de Supabase no válida o faltante',
      details: {
        type: 'config_error',
        message: 'Variables de entorno de Supabase no configuradas correctamente'
      }
    };
  }

  try {
    // First, try a simple connectivity test with a shorter timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout
    
    try {
      const response = await fetch(`${supabaseUrl}/rest/v1/`, {
        method: 'HEAD',
        headers: {
          'apikey': supabaseKey,
          'Authorization': `Bearer ${supabaseKey}`
        },
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        return {
          isConnected: false,
          error: `HTTP ${response.status}: ${response.statusText}`,
          details: {
            status: response.status,
            statusText: response.statusText,
            url: supabaseUrl
          }
        };
      }
    } catch (fetchError: any) {
      clearTimeout(timeoutId);
      
      // Handle specific webcontainer/network errors
      if (fetchError.name === 'AbortError') {
        return {
          isConnected: false,
          error: 'Tiempo de conexión agotado',
          details: {
            type: 'timeout',
            message: 'El entorno puede tener restricciones de red'
          }
        };
      }
      
      if (fetchError.message.includes('Failed to fetch')) {
        return {
          isConnected: false,
          error: 'Error de red - No se puede conectar a Supabase',
          details: {
            type: 'network_error',
            message: 'Esto puede deberse a restricciones de red del entorno',
            suggestion: 'Intenta desplegar en un entorno de producción'
          }
        };
      }
      
      throw fetchError;
    }

    return { isConnected: true };
  } catch (error: any) {
    return {
      isConnected: false,
      error: error.message || 'Error de conexión desconocido',
      details: {
        type: 'unexpected_error',
        error: error
      }
    };
  }
};

// Enhanced query executor with better error handling
export const executeQuery = async <T>(
  query: () => Promise<{ data: T | null; error: any }>,
  maxRetries = 2
): Promise<{ data: T | null; error: any; diagnostics?: any }> => {
  let lastError = null;
  let diagnostics = {};

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      const result = await query();
      
      if (!result.error) {
        return { ...result, diagnostics };
      }

      lastError = result.error;
      diagnostics = {
        ...diagnostics,
        [`attempt${attempt}`]: {
          timestamp: new Date().toISOString(),
          error: result.error,
        }
      };

      // Don't retry on certain types of errors
      if (result.error?.message?.includes('Failed to fetch') || 
          result.error?.code === 'PGRST301' ||
          result.error?.code === '401') {
        break;
      }

      if (attempt < maxRetries) {
        const backoff = Math.min(500 * attempt, 2000);
        await new Promise(resolve => setTimeout(resolve, backoff));
      }
    } catch (error) {
      lastError = error;
      diagnostics = {
        ...diagnostics,
        [`attempt${attempt}`]: {
          timestamp: new Date().toISOString(),
          error: (error as Error).message,
        }
      };

      if (attempt === maxRetries) {
        break;
      }
      
      const backoff = Math.min(500 * attempt, 2000);
      await new Promise(resolve => setTimeout(resolve, backoff));
    }
  }

  return { 
    data: null, 
    error: lastError, 
    diagnostics 
  };
};